import { z } from 'zod'

/**
 * FO-093 (batch-21) — Contractbeheer relaties. Gedeeld tussen API (validatie +
 * read-DTO) en backoffice (form-validatie + tab-rendering). Eén bron, getoond op
 * de relatie/CRM-pagina én de acquisitie-lead-pagina (via clientId).
 *
 * Bedragen reizen als Decimal-string (nooit Float); datums als jjjj-mm-dd. DB
 * slaat UTC op, maand/jaar/verloop wordt in Europe/Amsterdam afgeleid.
 */

// ─── Enums (string-unions, gelijk aan de Prisma-enums) ──────────────────────

export type ClientContractType =
  | 'RAAMOVEREENKOMST'
  | 'SLA'
  | 'PRIJSAFSPRAAK'
  | 'MANTELOVEREENKOMST'
  // FO-400 (batch-49) — projectovereenkomst vanuit de acquisitie-flow.
  | 'PROJECTOVEREENKOMST'
  | 'OVERIG'

export type ClientContractStatus =
  | 'CONCEPT'
  | 'INTERN_AKKOORD'
  | 'VERSTUURD'
  | 'GETEKEND'
  | 'ACTIEF'
  | 'VERLOPEN'
  | 'OPGEZEGD'

export type ClientContractAuditActie =
  | 'AANGEMAAKT'
  | 'STATUS_GEWIJZIGD'
  | 'VERSIE_TOEGEVOEGD'
  | 'VERLENGD'
  | 'VERWIJDERD'
  // batch-58 klant-portaal-beheer (FO-428) — de beheerder zet de klantportaal-
  // zichtbaarheid van deze overeenkomst expliciet aan of uit.
  | 'KLANTPORTAAL_VRIJGAVE'

export const CLIENT_CONTRACT_TYPES: readonly ClientContractType[] = [
  'RAAMOVEREENKOMST',
  'SLA',
  'PRIJSAFSPRAAK',
  'MANTELOVEREENKOMST',
  'PROJECTOVEREENKOMST',
  'OVERIG',
] as const

export const CLIENT_CONTRACT_TYPE_LABELS: Record<ClientContractType, string> = {
  RAAMOVEREENKOMST: 'Raamovereenkomst',
  SLA: 'SLA',
  PRIJSAFSPRAAK: 'Prijsafspraak',
  MANTELOVEREENKOMST: 'Mantelovereenkomst',
  PROJECTOVEREENKOMST: 'Projectovereenkomst',
  OVERIG: 'Overig',
}

export const CLIENT_CONTRACT_STATUS_LABELS: Record<ClientContractStatus, string> = {
  CONCEPT: 'Concept',
  INTERN_AKKOORD: 'Intern akkoord',
  VERSTUURD: 'Verstuurd',
  GETEKEND: 'Getekend',
  ACTIEF: 'Actief',
  VERLOPEN: 'Verlopen',
  OPGEZEGD: 'Opgezegd',
}

export const CLIENT_CONTRACT_STATUS_BADGE: Record<
  ClientContractStatus,
  'secondary' | 'info' | 'warning' | 'success' | 'danger'
> = {
  CONCEPT: 'secondary',
  INTERN_AKKOORD: 'info',
  VERSTUURD: 'info',
  GETEKEND: 'warning',
  ACTIEF: 'success',
  VERLOPEN: 'danger',
  OPGEZEGD: 'secondary',
}

export const CLIENT_CONTRACT_AUDIT_LABELS: Record<ClientContractAuditActie, string> = {
  AANGEMAAKT: 'Aangemaakt',
  STATUS_GEWIJZIGD: 'Status gewijzigd',
  VERSIE_TOEGEVOEGD: 'Versie toegevoegd',
  VERLENGD: 'Verlengd',
  VERWIJDERD: 'Verwijderd',
  KLANTPORTAAL_VRIJGAVE: 'Klantportaal-vrijgave gewijzigd',
}

// ─── Status-workflow ─────────────────────────────────────────────────────────
//
// Lineaire voorwaartse keten + terminale OPGEZEGD. De primaire knop brengt het
// contract één stap vooruit. Achteruit (terugzetten) en OPGEZEGD vereisen
// `CRM.canApprove`; ook de intern-akkoord-stap (management keurt intern goed door
// te markeren als verstuurd) vereist `CRM.canApprove`.

export const CLIENT_CONTRACT_STATUS_ORDER: readonly ClientContractStatus[] = [
  'CONCEPT',
  'INTERN_AKKOORD',
  'VERSTUURD',
  'GETEKEND',
  'ACTIEF',
  'VERLOPEN',
] as const

/** Volgende voorwaartse stap + knoplabel per status (geen entry = geen voorwaartse knop). */
export const CLIENT_CONTRACT_NEXT_STEP: Partial<
  Record<ClientContractStatus, { next: ClientContractStatus; label: string }>
> = {
  CONCEPT: { next: 'INTERN_AKKOORD', label: 'Naar intern akkoord' },
  INTERN_AKKOORD: { next: 'VERSTUURD', label: 'Markeer verstuurd' },
  VERSTUURD: { next: 'GETEKEND', label: 'Markeer getekend' },
  GETEKEND: { next: 'ACTIEF', label: 'Activeer' },
}

/** Een overgang is geldig als hij vooruit één stap is, een stap terug in de keten, of OPGEZEGD. */
export function isValidContractTransition(
  from: ClientContractStatus,
  to: ClientContractStatus,
): boolean {
  if (from === to) return false
  if (to === 'OPGEZEGD') return from !== 'OPGEZEGD'
  if (from === 'OPGEZEGD') return false
  const fi = CLIENT_CONTRACT_STATUS_ORDER.indexOf(from)
  const ti = CLIENT_CONTRACT_STATUS_ORDER.indexOf(to)
  if (fi === -1 || ti === -1) return false
  // Eén stap vooruit (primaire knop) of een stap terug (terugzetten, canApprove).
  return ti === fi + 1 || ti < fi
}

/**
 * Vereist deze overgang `CRM.canApprove`? Ja bij: elke achteruit-zet, OPGEZEGD,
 * en de intern-akkoord-stap (INTERN_AKKOORD -> VERSTUURD = management keurt goed).
 * De overige voorwaartse stappen volstaan met `CRM.canEdit`.
 */
export function contractTransitionRequiresApprove(
  from: ClientContractStatus,
  to: ClientContractStatus,
): boolean {
  if (to === 'OPGEZEGD') return true
  if (from === 'INTERN_AKKOORD' && to === 'VERSTUURD') return true
  const fi = CLIENT_CONTRACT_STATUS_ORDER.indexOf(from)
  const ti = CLIENT_CONTRACT_STATUS_ORDER.indexOf(to)
  if (fi === -1 || ti === -1) return false
  return ti < fi // achteruit
}

// ─── Zod-schemas (gedeeld API + form) ───────────────────────────────────────

const dateOnly = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum moet jjjj-mm-dd zijn')

// Bedragen als Decimal-string (de form normaliseert komma -> punt vóór versturen).
const geldBedrag = z
  .string()
  .trim()
  .regex(/^\d+(\.\d{1,2})?$/, 'Bedrag moet een geldig getal zijn (bv. 12500.00)')

const percentage = z
  .string()
  .trim()
  .regex(/^\d+(\.\d{1,2})?$/, 'Percentage moet een geldig getal zijn (bv. 2.50)')

// Geëxporteerd zodat de FO-139 Overeenkomsten-laag (packages/types/overeenkomsten.ts)
// dezelfde basisvelden hergebruikt i.p.v. te dupliceren (één bron van waarheid).
export const clientContractBase = z.object({
  titel: z.string().trim().min(1, 'Titel is verplicht'),
  // Eén bron: de Zod-enum volgt CLIENT_CONTRACT_TYPES, zodat een nieuw type
  // (FO-400: PROJECTOVEREENKOMST) nooit half doorgevoerd kan raken.
  type: z.enum(CLIENT_CONTRACT_TYPES as unknown as [ClientContractType, ...ClientContractType[]]),
  ingangsdatum: dateOnly,
  einddatum: dateOnly.nullable().optional(),
  looptijdMaanden: z.number().int().positive().nullable().optional(),
  opzegtermijnDagen: z.number().int().nonnegative().nullable().optional(),
  contractbedrag: geldBedrag.nullable().optional(),
  indexeringPct: percentage.nullable().optional(),
  responsibleEmployeeId: z.string().min(1).nullable().optional(),
  notitie: z.string().nullable().optional(),
})

// einddatum mag niet vóór ingangsdatum liggen (jjjj-mm-dd vergelijkt chronologisch).
const einddatumNaIngang = (
  data: { ingangsdatum?: string; einddatum?: string | null },
): boolean => {
  if (!data.ingangsdatum || !data.einddatum) return true
  return data.einddatum >= data.ingangsdatum
}
const einddatumError: { message: string; path: string[] } = {
  message: 'Einddatum mag niet vóór de ingangsdatum liggen',
  path: ['einddatum'],
}

export const CreateClientContractSchema = clientContractBase.refine(einddatumNaIngang, einddatumError)
export type CreateClientContractInput = z.infer<typeof CreateClientContractSchema>

export const UpdateClientContractSchema = clientContractBase
  .partial()
  .refine(einddatumNaIngang, einddatumError)
export type UpdateClientContractInput = z.infer<typeof UpdateClientContractSchema>

export const ContractStatusTransitionSchema = z.object({
  status: z.enum([
    'CONCEPT',
    'INTERN_AKKOORD',
    'VERSTUURD',
    'GETEKEND',
    'ACTIEF',
    'VERLOPEN',
    'OPGEZEGD',
  ]),
  toelichting: z.string().trim().max(2000).optional(),
})
export type ContractStatusTransitionInput = z.infer<typeof ContractStatusTransitionSchema>

export const VerlengContractSchema = z.object({
  einddatum: dateOnly,
  contractbedrag: geldBedrag.nullable().optional(),
  toelichting: z.string().trim().max(2000).optional(),
})
export type VerlengContractInput = z.infer<typeof VerlengContractSchema>

// Nieuwe contract-versie = multipart-upload van het PDF; de API maakt het
// Document (scope CLIENT, categorie CONTRACT) + de ClientContractVersie. Geen
// JSON-body-schema nodig (alleen het bestand).

// ─── Read-DTOs ───────────────────────────────────────────────────────────────

export interface ClientContractVersieDTO {
  id: string
  versieNr: number
  isActief: boolean
  documentId: string
  documentNaam: string | null
  downloadUrl: string | null
  createdAt: string
}

export interface ClientContractAuditDTO {
  id: string
  actie: ClientContractAuditActie
  actorNaam: string | null
  oudeStatus: ClientContractStatus | null
  nieuweStatus: ClientContractStatus | null
  toelichting: string | null
  createdAt: string
}

export interface ClientContractDTO {
  id: string
  clientId: string
  titel: string
  type: ClientContractType
  status: ClientContractStatus
  ingangsdatum: string
  einddatum: string | null
  looptijdMaanden: number | null
  opzegtermijnDagen: number | null
  /** Decimal-strings (nooit Float). `null` = niet ingevuld. */
  contractbedrag: string | null
  indexeringPct: string | null
  responsibleEmployeeId: string | null
  responsibleEmployee: { id: string; firstName: string; lastName: string } | null
  notitie: string | null
  internAkkoordOp: string | null
  verstuurdOp: string | null
  getekendOp: string | null
  geactiveerdOp: string | null
  /** Dagen tot einddatum (Europe/Amsterdam); negatief = verlopen; `null` = geen einddatum. */
  dagenTotEinde: number | null
  createdAt: string
  updatedAt: string
  versies: ClientContractVersieDTO[]
  audits: ClientContractAuditDTO[]
}
